import React, { useEffect, useMemo, useState } from "react";
import {
  Search,
  MapPin,
  BriefcaseBusiness,
  Mail,
  Phone,
  Star,
  UserRound,
  RefreshCw,
  X,
  Plus,
  Loader2,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";

const API_URL = "/api/trainers/all";
const CREATE_API_URL = "/api/trainers/save";

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  experience: "",
  city: "",
  state: "",
  skills: "",
  source: "Freelancer",
  remark: "",
};

const FreelancePro = () => {
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");

  const [selectedTrainer, setSelectedTrainer] = useState(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [form, setForm] = useState(emptyForm);

  /* =========================================================
     FETCH FREELANCERS
  ========================================================= */

  const fetchTrainers = async (showRefreshLoader = false) => {
    try {
      if (showRefreshLoader) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      const response = await fetch(API_URL);

      if (!response.ok) {
        throw new Error(`Failed to load freelancers (${response.status})`);
      }

      const result = await response.json();

      let data = [];

      if (Array.isArray(result)) {
        data = result;
      } else if (Array.isArray(result?.data)) {
        data = result.data;
      } else if (Array.isArray(result?.trainers)) {
        data = result.trainers;
      } else if (Array.isArray(result?.content)) {
        data = result.content;
      }

      setTrainers(data);
    } catch (err) {
      console.error("Fetch freelancers error:", err);
      setError(err.message || "Unable to load freelancers.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchTrainers();
  }, []);

  /* =========================================================
     FORM HANDLERS
  ========================================================= */

  const updateForm = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (formError) {
      setFormError("");
    }

    if (successMessage) {
      setSuccessMessage("");
    }
  };

  const resetForm = () => {
    setForm(emptyForm);
    setFormError("");
  };

  /* =========================================================
     CREATE FREELANCER
  ========================================================= */

  const handleCreateFreelancer = async (e) => {
    e.preventDefault();

    setFormError("");
    setSuccessMessage("");

    if (!form.name.trim()) {
      setFormError("Please enter freelancer name.");
      return;
    }

    if (!form.email.trim()) {
      setFormError("Please enter email address.");
      return;
    }

    if (!form.phone.trim()) {
      setFormError("Please enter phone number.");
      return;
    }

    try {
      setSaving(true);

      const trainerPayload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        experience: form.experience
          ? Number(form.experience)
          : 0,
        city: form.city.trim(),
        state: form.state.trim(),
        skills: form.skills
          .split(",")
          .map((skill) => skill.trim())
          .filter(Boolean),
        source: form.source.trim() || "Freelancer",
        remark: form.remark.trim(),
        status: "PENDING",
      };

      const formData = new FormData();

      formData.append(
        "trainer",
        new Blob([JSON.stringify(trainerPayload)], {
          type: "application/json",
        })
      );

      const response = await fetch(CREATE_API_URL, {
        method: "POST",
        body: formData,
      });

      const contentType = response.headers.get("content-type");

      let result;

      if (contentType?.includes("application/json")) {
        result = await response.json();
      } else {
        result = await response.text();
      }

      if (!response.ok) {
        let message = "Failed to create freelancer.";

        if (typeof result === "string" && result.trim()) {
          message = result;
        } else if (result?.message) {
          message = result.message;
        } else if (result?.error) {
          message = result.error;
        }

        throw new Error(message);
      }

      setSuccessMessage(
        "Freelancer account created successfully."
      );

      resetForm();

      await fetchTrainers(true);

      setTimeout(() => {
        setShowCreateModal(false);
        setSuccessMessage("");
      }, 700);
    } catch (err) {
      console.error("Create freelancer error:", err);

      setFormError(
        err.message ||
          "Something went wrong while creating freelancer."
      );
    } finally {
      setSaving(false);
    }
  };

  /* =========================================================
     NORMALIZE SKILLS
  ========================================================= */

  const getSkills = (trainer) => {
    const skills = trainer?.skills;

    if (!skills) {
      return [];
    }

    if (Array.isArray(skills)) {
      return skills
        .flatMap((item) => {
          if (typeof item === "string") {
            return item.split(",");
          }

          if (typeof item === "object" && item !== null) {
            return [
              item.name ||
                item.skillName ||
                item.skill ||
                item.title ||
                "",
            ];
          }

          return [];
        })
        .map((item) => String(item).trim())
        .filter(Boolean);
    }

    if (typeof skills === "string") {
      try {
        const parsed = JSON.parse(skills);

        if (Array.isArray(parsed)) {
          return parsed
            .map((item) => {
              if (typeof item === "string") {
                return item;
              }

              if (typeof item === "object" && item !== null) {
                return (
                  item.name ||
                  item.skillName ||
                  item.skill ||
                  item.title ||
                  ""
                );
              }

              return "";
            })
            .map((item) => String(item).trim())
            .filter(Boolean);
        }
      } catch (error) {
        // Normal comma-separated string
      }

      return skills
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }

    return [];
  };

  /* =========================================================
     FILTER
  ========================================================= */

  const filteredTrainers = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) {
      return trainers;
    }

    return trainers.filter((trainer) => {
      const skills = getSkills(trainer).join(" ");

      const searchableText = [
        trainer?.name,
        trainer?.email,
        trainer?.phone,
        trainer?.city,
        trainer?.state,
        trainer?.source,
        trainer?.remark,
        skills,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(query);
    });
  }, [trainers, search]);

  /* =========================================================
     FORMAT EXPERIENCE
  ========================================================= */

  const formatExperience = (experience) => {
    if (
      experience === null ||
      experience === undefined ||
      experience === ""
    ) {
      return "Experience not specified";
    }

    return `${experience} ${
      Number(experience) === 1 ? "Year" : "Years"
    } Experience`;
  };

  /* =========================================================
     LOADING
  ========================================================= */

  if (loading) {
    return (
      <>
        <style>{styles}</style>

        <div className="fp-page fp-loading-page">
          <div className="fp-loader-box">
            <Loader2 className="fp-spinner" size={34} />
            <h3>Loading Freelancers...</h3>
            <p>Please wait while we fetch freelancer profiles.</p>
          </div>
        </div>
      </>
    );
  }

  /* =========================================================
     MAIN UI
  ========================================================= */

  return (
    <>
      <style>{styles}</style>

      <div className="fp-page">

        {/* =====================================================
            HEADER
        ===================================================== */}

        <div className="fp-header">
          <div>
            <div className="fp-title-row">
              <div className="fp-title-icon">
                <BriefcaseBusiness size={24} />
              </div>

              <div>
                <h1>Freelance Trainers</h1>
                <p>
                  Manage, create and view your freelance trainer
                  profiles
                </p>
              </div>
            </div>
          </div>

          <div className="fp-header-actions">

            <button
              className="fp-refresh-btn"
              onClick={() => fetchTrainers(true)}
              disabled={refreshing}
            >
              <RefreshCw
                size={17}
                className={refreshing ? "fp-spin" : ""}
              />

              <span>Refresh</span>
            </button>

            <button
              className="fp-add-btn"
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
            >
              <Plus size={18} />

              <span>Add Freelancer</span>
            </button>

            <div className="fp-count-box">
              <strong>{trainers.length}</strong>
              <span>Total Freelancers</span>
            </div>

          </div>
        </div>

        {/* =====================================================
            SUCCESS
        ===================================================== */}

        {successMessage && (
          <div className="fp-success-alert">
            <CheckCircle2 size={19} />
            <span>{successMessage}</span>
          </div>
        )}

        {/* =====================================================
            ERROR
        ===================================================== */}

        {error && (
          <div className="fp-error-alert">
            <AlertCircle size={19} />

            <div>
              <strong>Unable to load freelancers</strong>
              <p>{error}</p>
            </div>

            <button
              onClick={() => fetchTrainers()}
              className="fp-error-retry"
            >
              Retry
            </button>
          </div>
        )}

        {/* =====================================================
            SEARCH
        ===================================================== */}

        <div className="fp-toolbar">

          <div className="fp-search-box">
            <Search size={19} />

            <input
              type="text"
              placeholder="Search by name, email, city, state, skills..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            {search && (
              <button
                className="fp-search-clear"
                onClick={() => setSearch("")}
              >
                <X size={16} />
              </button>
            )}
          </div>

          <div className="fp-result-text">
            Showing{" "}
            <strong>{filteredTrainers.length}</strong>{" "}
            freelancer
            {filteredTrainers.length !== 1 ? "s" : ""}
          </div>

        </div>

        {/* =====================================================
            EMPTY STATE
        ===================================================== */}

        {filteredTrainers.length === 0 ? (
          <div className="fp-empty-state">

            <div className="fp-empty-icon">
              <UserRound size={42} />
            </div>

            <h2>
              {search
                ? "No freelancers found"
                : "No freelancers available"}
            </h2>

            <p>
              {search
                ? "Try changing your search keywords."
                : "Create your first freelancer account to get started."}
            </p>

            {!search && (
              <button
                className="fp-empty-add-btn"
                onClick={() => {
                  resetForm();
                  setShowCreateModal(true);
                }}
              >
                <Plus size={18} />
                Add Freelancer
              </button>
            )}
          </div>
        ) : (
          /* =====================================================
             CARDS
          ===================================================== */

          <div className="fp-grid">

            {filteredTrainers.map((trainer, index) => {

              const skills = getSkills(trainer);

              return (
                <div
                  className="fp-card"
                  key={trainer?.id || index}
                >

                  {/* CARD TOP */}

                  <div className="fp-card-top">

                    <div className="fp-avatar">
                      {trainer?.name
                        ? trainer.name
                            .trim()
                            .charAt(0)
                            .toUpperCase()
                        : "F"}
                    </div>

                    <div className="fp-card-heading">

                      <h3>
                        {trainer?.name ||
                          "Freelancer"}
                      </h3>

                      <span
                        className={`fp-status ${
                          String(
                            trainer?.status || "ACTIVE"
                          ).toLowerCase()
                        }`}
                      >
                        {trainer?.status ||
                          "ACTIVE"}
                      </span>

                    </div>

                  </div>

                  {/* SKILLS */}

                  {skills.length > 0 && (
                    <div className="fp-skills">

                      {skills.slice(0, 5).map(
                        (skill, skillIndex) => (
                          <span
                            key={`${skill}-${skillIndex}`}
                            className="fp-skill"
                          >
                            {skill}
                          </span>
                        )
                      )}

                      {skills.length > 5 && (
                        <span className="fp-more-skill">
                          +{skills.length - 5}
                        </span>
                      )}

                    </div>
                  )}

                  {/* DETAILS */}

                  <div className="fp-details">

                    <div className="fp-detail">

                      <BriefcaseBusiness size={16} />

                      <span>
                        {formatExperience(
                          trainer?.experience
                        )}
                      </span>

                    </div>

                    {(trainer?.city ||
                      trainer?.state) && (
                      <div className="fp-detail">

                        <MapPin size={16} />

                        <span>
                          {[
                            trainer?.city,
                            trainer?.state,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </span>

                      </div>
                    )}

                    {trainer?.email && (
                      <div className="fp-detail">

                        <Mail size={16} />

                        <span title={trainer.email}>
                          {trainer.email}
                        </span>

                      </div>
                    )}

                    {trainer?.phone && (
                      <div className="fp-detail">

                        <Phone size={16} />

                        <span>
                          {trainer.phone}
                        </span>

                      </div>
                    )}

                  </div>

                  {/* CARD FOOTER */}

                  <div className="fp-card-footer">

                    <div className="fp-professional">

                      <div className="fp-professional-icon">
                        <Star size={14} />
                      </div>

                      <span>
                        Professional Trainer
                      </span>

                    </div>

                    <button
                      className="fp-view-btn"
                      onClick={() =>
                        setSelectedTrainer(
                          trainer
                        )
                      }
                    >
                      View Profile
                    </button>

                  </div>

                </div>
              );
            })}

          </div>
        )}

        {/* =====================================================
            CREATE FREELANCER MODAL
        ===================================================== */}

        {showCreateModal && (
          <div
            className="fp-modal-overlay"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget && !saving) {
                setShowCreateModal(false);
              }
            }}
          >

            <div className="fp-create-modal">

              {/* MODAL HEADER */}

              <div className="fp-modal-header">

                <div>
                  <h2>Create Freelancer Account</h2>

                  <p>
                    Add a new freelancer trainer
                    to the platform.
                  </p>
                </div>

                <button
                  className="fp-modal-close"
                  onClick={() => {
                    if (!saving) {
                      setShowCreateModal(false);
                    }
                  }}
                  disabled={saving}
                >
                  <X size={21} />
                </button>

              </div>

              {/* FORM */}

              <form onSubmit={handleCreateFreelancer}>

                <div className="fp-form-body">

                  {/* BASIC */}

                  <div className="fp-section-title">
                    <div className="fp-section-number">
                      1
                    </div>

                    <div>
                      <h3>Basic Information</h3>
                      <p>
                        Enter the freelancer's
                        contact details.
                      </p>
                    </div>
                  </div>

                  <div className="fp-form-grid">

                    <div className="fp-form-group fp-full">

                      <label>
                        Full Name{" "}
                        <span>*</span>
                      </label>

                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) =>
                          updateForm(
                            "name",
                            e.target.value
                          )
                        }
                        placeholder="Enter freelancer name"
                        required
                      />

                    </div>

                    <div className="fp-form-group">

                      <label>
                        Email{" "}
                        <span>*</span>
                      </label>

                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) =>
                          updateForm(
                            "email",
                            e.target.value
                          )
                        }
                        placeholder="example@email.com"
                        required
                      />

                    </div>

                    <div className="fp-form-group">

                      <label>
                        Phone{" "}
                        <span>*</span>
                      </label>

                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) =>
                          updateForm(
                            "phone",
                            e.target.value
                          )
                        }
                        placeholder="+91 XXXXX XXXXX"
                        required
                      />

                    </div>

                    <div className="fp-form-group">

                      <label>
                        Experience
                      </label>

                      <input
                        type="number"
                        min="0"
                        value={form.experience}
                        onChange={(e) =>
                          updateForm(
                            "experience",
                            e.target.value
                          )
                        }
                        placeholder="Years"
                      />

                    </div>

                    <div className="fp-form-group">

                      <label>City</label>

                      <input
                        type="text"
                        value={form.city}
                        onChange={(e) =>
                          updateForm(
                            "city",
                            e.target.value
                          )
                        }
                        placeholder="Enter city"
                      />

                    </div>

                    <div className="fp-form-group">

                      <label>State</label>

                      <input
                        type="text"
                        value={form.state}
                        onChange={(e) =>
                          updateForm(
                            "state",
                            e.target.value
                          )
                        }
                        placeholder="Enter state"
                      />

                    </div>

                  </div>

                  {/* PROFESSIONAL */}

                  <div className="fp-section-title fp-section-second">

                    <div className="fp-section-number">
                      2
                    </div>

                    <div>
                      <h3>Professional Details</h3>

                      <p>
                        Add skills and additional
                        information.
                      </p>
                    </div>

                  </div>

                  <div className="fp-form-grid">

                    <div className="fp-form-group fp-full">

                      <label>
                        Skills
                      </label>

                      <input
                        type="text"
                        value={form.skills}
                        onChange={(e) =>
                          updateForm(
                            "skills",
                            e.target.value
                          )
                        }
                        placeholder="Java, Spring Boot, AWS, Python"
                      />

                      <small>
                        Enter multiple skills separated
                        by commas.
                      </small>

                    </div>

                    <div className="fp-form-group">

                      <label>
                        Source
                      </label>

                      <select
                        value={form.source}
                        onChange={(e) =>
                          updateForm(
                            "source",
                            e.target.value
                          )
                        }
                      >
                        <option value="Freelancer">
                          Freelancer
                        </option>

                        <option value="LinkedIn">
                          LinkedIn
                        </option>

                        <option value="Reference">
                          Reference
                        </option>

                        <option value="Website">
                          Website
                        </option>

                        <option value="Other">
                          Other
                        </option>
                      </select>

                    </div>

                    <div className="fp-form-group">

                      <label>
                        Remark
                      </label>

                      <input
                        type="text"
                        value={form.remark}
                        onChange={(e) =>
                          updateForm(
                            "remark",
                            e.target.value
                          )
                        }
                        placeholder="Optional remark"
                      />

                    </div>

                  </div>

                  {/* ERROR */}

                  {formError && (
                    <div className="fp-form-error">

                      <AlertCircle size={18} />

                      <span>
                        {formError}
                      </span>

                    </div>
                  )}

                </div>

                {/* MODAL FOOTER */}

                <div className="fp-modal-footer">

                  <button
                    type="button"
                    className="fp-cancel-btn"
                    onClick={() => {
                      if (!saving) {
                        setShowCreateModal(false);
                      }
                    }}
                    disabled={saving}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="fp-create-btn"
                    disabled={saving}
                  >

                    {saving ? (
                      <>
                        <Loader2
                          size={18}
                          className="fp-spin"
                        />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Plus size={18} />
                        Create Freelancer
                      </>
                    )}

                  </button>

                </div>

              </form>

            </div>
          </div>
        )}

        {/* =====================================================
            PROFILE MODAL
        ===================================================== */}

        {selectedTrainer && (
          <div
            className="fp-modal-overlay"
            onMouseDown={(e) => {
              if (
                e.target === e.currentTarget
              ) {
                setSelectedTrainer(null);
              }
            }}
          >

            <div className="fp-profile-modal">

              <button
                className="fp-profile-close"
                onClick={() =>
                  setSelectedTrainer(null)
                }
              >
                <X size={21} />
              </button>

              <div className="fp-profile-header">

                <div className="fp-profile-avatar">
                  {selectedTrainer?.name
                    ?.charAt(0)
                    ?.toUpperCase() || "F"}
                </div>

                <div>

                  <h2>
                    {selectedTrainer?.name ||
                      "Freelancer"}
                  </h2>

                  <span className="fp-profile-role">
                    Professional Freelance Trainer
                  </span>

                </div>

              </div>

              <div className="fp-profile-info-grid">

                <div className="fp-profile-info">

                  <Mail size={18} />

                  <div>
                    <span>Email</span>
                    <strong>
                      {selectedTrainer?.email ||
                        "Not available"}
                    </strong>
                  </div>

                </div>

                <div className="fp-profile-info">

                  <Phone size={18} />

                  <div>
                    <span>Phone</span>
                    <strong>
                      {selectedTrainer?.phone ||
                        "Not available"}
                    </strong>
                  </div>

                </div>

                <div className="fp-profile-info">

                  <BriefcaseBusiness size={18} />

                  <div>
                    <span>Experience</span>
                    <strong>
                      {formatExperience(
                        selectedTrainer?.experience
                      )}
                    </strong>
                  </div>

                </div>

                <div className="fp-profile-info">

                  <MapPin size={18} />

                  <div>
                    <span>Location</span>

                    <strong>
                      {[
                        selectedTrainer?.city,
                        selectedTrainer?.state,
                      ]
                        .filter(Boolean)
                        .join(", ") ||
                        "Not available"}
                    </strong>

                  </div>

                </div>

              </div>

              <div className="fp-profile-section">

                <h3>Skills</h3>

                <div className="fp-profile-skills">

                  {getSkills(
                    selectedTrainer
                  ).length > 0 ? (
                    getSkills(
                      selectedTrainer
                    ).map((skill, index) => (
                      <span key={index}>
                        {skill}
                      </span>
                    ))
                  ) : (
                    <p>
                      No skills added.
                    </p>
                  )}

                </div>

              </div>

              {selectedTrainer?.remark && (
                <div className="fp-profile-section">

                  <h3>Remark</h3>

                  <p className="fp-remark">
                    {selectedTrainer.remark}
                  </p>

                </div>
              )}

              <div className="fp-profile-footer">

                <span>
                  Status:{" "}
                  <strong>
                    {selectedTrainer?.status ||
                      "ACTIVE"}
                  </strong>
                </span>

                <button
                  onClick={() =>
                    setSelectedTrainer(null)
                  }
                >
                  Close
                </button>

              </div>

            </div>
          </div>
        )}

      </div>
    </>
  );
};

/* =============================================================
   CSS
============================================================= */

const styles = `
* {
  box-sizing: border-box;
}

.fp-page {
  min-height: 100vh;
  padding: 28px;
  margin-top: 90px;
  background: #f7f7fb;
  color: #202124;
}

/* =========================================================
   HEADER
========================================================= */

.fp-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  margin-bottom: 24px;
}

.fp-title-row {
  display: flex;
  align-items: center;
  gap: 14px;
}

.fp-title-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #3E0F8D;
  color: white;
  box-shadow: 0 8px 20px rgba(62, 15, 141, 0.18);
}

.fp-header h1 {
  margin: 0;
  font-size: 26px;
  font-weight: 800;
  color: #25153f;
}

.fp-header p {
  margin: 5px 0 0;
  color: #747084;
  font-size: 14px;
}

.fp-header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.fp-refresh-btn,
.fp-add-btn {
  height: 44px;
  border-radius: 11px;
  padding: 0 15px;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  cursor: pointer;
  transition: 0.2s ease;
}

.fp-refresh-btn {
  background: white;
  color: #4d455a;
  border: 1px solid #e5e1ed;
}

.fp-refresh-btn:hover {
  border-color: #9564DD;
  color: #3E0F8D;
}

.fp-refresh-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.fp-add-btn {
  background: #3E0F8D;
  color: white;
  box-shadow: 0 7px 18px rgba(62, 15, 141, 0.2);
}

.fp-add-btn:hover {
  transform: translateY(-1px);
  background: #4b18a2;
}

.fp-count-box {
  min-width: 125px;
  height: 44px;
  padding: 6px 13px;
  border-radius: 11px;
  background: white;
  border: 1px solid #e7e2ef;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.fp-count-box strong {
  color: #3E0F8D;
  font-size: 17px;
  line-height: 17px;
}

.fp-count-box span {
  font-size: 10px;
  color: #817a8d;
  margin-top: 2px;
}

/* =========================================================
   ALERTS
========================================================= */

.fp-success-alert,
.fp-error-alert {
  margin-bottom: 18px;
  border-radius: 12px;
  padding: 13px 15px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
}

.fp-success-alert {
  background: #effaf2;
  border: 1px solid #bce7c7;
  color: #247438;
}

.fp-error-alert {
  background: #fff3f2;
  border: 1px solid #f1cac6;
  color: #a02f24;
}

.fp-error-alert div {
  flex: 1;
}

.fp-error-alert strong {
  display: block;
}

.fp-error-alert p {
  margin: 3px 0 0;
  font-size: 12px;
}

.fp-error-retry {
  border: none;
  background: white;
  border: 1px solid #e3b6b2;
  border-radius: 8px;
  padding: 7px 12px;
  cursor: pointer;
  font-weight: 700;
  color: #a02f24;
}

/* =========================================================
   TOOLBAR
========================================================= */

.fp-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
  margin-bottom: 22px;
}

.fp-search-box {
  flex: 1;
  max-width: 700px;
  height: 48px;
  background: white;
  border: 1px solid #e3dfea;
  border-radius: 12px;
  display: flex;
  align-items: center;
  padding: 0 14px;
  gap: 10px;
  color: #8a8295;
}

.fp-search-box:focus-within {
  border-color: #9564DD;
  box-shadow: 0 0 0 3px rgba(149, 100, 221, 0.09);
}

.fp-search-box input {
  border: none;
  outline: none;
  flex: 1;
  background: transparent;
  font-size: 14px;
  color: #2a2530;
}

.fp-search-box input::placeholder {
  color: #aaa3b1;
}

.fp-search-clear {
  width: 28px;
  height: 28px;
  border: none;
  background: #f3f0f7;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #696170;
  cursor: pointer;
}

.fp-result-text {
  color: #77707f;
  font-size: 13px;
}

.fp-result-text strong {
  color: #3E0F8D;
}

/* =========================================================
   GRID
========================================================= */

.fp-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
}

.fp-card {
  background: white;
  border: 1px solid #e8e3ee;
  border-radius: 16px;
  padding: 19px;
  transition: 0.22s ease;
  box-shadow: 0 4px 15px rgba(33, 18, 48, 0.035);
}

.fp-card:hover {
  transform: translateY(-3px);
  border-color: #d5c7e7;
  box-shadow: 0 14px 30px rgba(62, 15, 141, 0.09);
}

.fp-card-top {
  display: flex;
  align-items: center;
  gap: 13px;
}

.fp-avatar {
  width: 51px;
  height: 51px;
  flex-shrink: 0;
  border-radius: 14px;
  background: linear-gradient(135deg, #3E0F8D, #9564DD);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 21px;
  font-weight: 800;
}

.fp-card-heading {
  flex: 1;
  min-width: 0;
}

.fp-card-heading h3 {
  margin: 0;
  color: #2b2035;
  font-size: 16px;
  font-weight: 800;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fp-status {
  display: inline-flex;
  margin-top: 6px;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.2px;
}

.fp-status.approved,
.fp-status.active {
  color: #23743e;
  background: #e9f8ed;
}

.fp-status.pending {
  color: #90610b;
  background: #fff5d9;
}

.fp-status.inactive {
  color: #69616f;
  background: #f0edf3;
}

.fp-status.blocked {
  color: #a52f28;
  background: #fff0ef;
}

/* =========================================================
   SKILLS
========================================================= */

.fp-skills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 17px;
}

.fp-skill,
.fp-more-skill {
  background: #f2ecfb;
  color: #5f31a4;
  border-radius: 7px;
  padding: 5px 8px;
  font-size: 11px;
  font-weight: 700;
}

.fp-more-skill {
  background: #f3f1f5;
  color: #706878;
}

/* =========================================================
   DETAILS
========================================================= */

.fp-details {
  margin-top: 17px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.fp-detail {
  display: flex;
  align-items: center;
  gap: 9px;
  color: #6f6877;
  min-width: 0;
  font-size: 12px;
}

.fp-detail svg {
  color: #9564DD;
  flex-shrink: 0;
}

.fp-detail span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* =========================================================
   FOOTER
========================================================= */

.fp-card-footer {
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid #eeeaf1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.fp-professional {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
}

.fp-professional span {
  font-size: 11px;
  color: #736b7d;
  white-space: nowrap;
}

.fp-professional-icon {
  width: 24px;
  height: 24px;
  border-radius: 7px;
  background: #faf4d4;
  color: #8e7b16;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fp-view-btn {
  border: none;
  background: #3E0F8D;
  color: white;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
}

.fp-view-btn:hover {
  background: #4c1ba0;
}

/* =========================================================
   EMPTY
========================================================= */

.fp-empty-state {
  min-height: 420px;
  background: white;
  border: 1px dashed #d8d0e0;
  border-radius: 17px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px;
  text-align: center;
}

.fp-empty-icon {
  width: 80px;
  height: 80px;
  border-radius: 22px;
  background: #f0eafa;
  color: #6e42b0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fp-empty-state h2 {
  margin: 17px 0 7px;
  color: #30243a;
}

.fp-empty-state p {
  margin: 0 0 20px;
  color: #817989;
  font-size: 13px;
}

.fp-empty-add-btn {
  display: flex;
  align-items: center;
  gap: 7px;
  border: none;
  background: #3E0F8D;
  color: white;
  padding: 10px 15px;
  border-radius: 9px;
  font-weight: 700;
  cursor: pointer;
}

/* =========================================================
   MODAL
========================================================= */

.fp-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(24, 18, 31, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  z-index: 9999;
}

.fp-create-modal {
  width: min(760px, 100%);
  max-height: 92vh;
  overflow-y: auto;
  background: white;
  border-radius: 19px;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.22);
}

.fp-modal-header {
  padding: 22px 24px;
  border-bottom: 1px solid #ece8f0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
}

.fp-modal-header h2 {
  margin: 0;
  font-size: 21px;
  color: #2b2036;
}

.fp-modal-header p {
  margin: 5px 0 0;
  color: #827a8a;
  font-size: 12px;
}

.fp-modal-close,
.fp-profile-close {
  width: 35px;
  height: 35px;
  border: none;
  background: #f3f0f5;
  color: #675e6d;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.fp-modal-close:hover,
.fp-profile-close:hover {
  background: #eae4ef;
}

.fp-form-body {
  padding: 24px;
}

.fp-section-title {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 17px;
}

.fp-section-second {
  margin-top: 28px;
}

.fp-section-number {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  flex-shrink: 0;
  background: #eee8f8;
  color: #3E0F8D;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 12px;
}

.fp-section-title h3 {
  margin: 0;
  color: #30243a;
  font-size: 14px;
}

.fp-section-title p {
  margin: 4px 0 0;
  color: #8b8290;
  font-size: 11px;
}

.fp-form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.fp-form-group {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.fp-form-group.fp-full {
  grid-column: 1 / -1;
}

.fp-form-group label {
  font-size: 12px;
  color: #4b4253;
  font-weight: 700;
}

.fp-form-group label span {
  color: #c83b32;
}

.fp-form-group input,
.fp-form-group select,
.fp-form-group textarea {
  height: 43px;
  border: 1px solid #ddd7e4;
  border-radius: 9px;
  outline: none;
  padding: 0 12px;
  font-size: 13px;
  color: #29222f;
  background: white;
  transition: 0.2s ease;
}

.fp-form-group input:focus,
.fp-form-group select:focus,
.fp-form-group textarea:focus {
  border-color: #9564DD;
  box-shadow: 0 0 0 3px rgba(149, 100, 221, 0.09);
}

.fp-form-group small {
  color: #928995;
  font-size: 10px;
}

.fp-form-error {
  margin-top: 18px;
  padding: 11px 13px;
  border-radius: 9px;
  background: #fff2f0;
  border: 1px solid #f1cbc7;
  color: #a33128;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
}

.fp-modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #ece8f0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.fp-cancel-btn,
.fp-create-btn {
  height: 42px;
  border-radius: 9px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  gap: 7px;
  font-weight: 700;
  cursor: pointer;
}

.fp-cancel-btn {
  background: white;
  border: 1px solid #ddd6e4;
  color: #655d6d;
}

.fp-create-btn {
  background: #3E0F8D;
  border: 1px solid #3E0F8D;
  color: white;
}

.fp-create-btn:hover {
  background: #4c1ba0;
}

.fp-create-btn:disabled,
.fp-cancel-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

/* =========================================================
   PROFILE MODAL
========================================================= */

.fp-profile-modal {
  width: min(610px, 100%);
  max-height: 90vh;
  overflow-y: auto;
  background: white;
  border-radius: 18px;
  padding: 25px;
  position: relative;
}

.fp-profile-close {
  position: absolute;
  top: 18px;
  right: 18px;
}

.fp-profile-header {
  display: flex;
  align-items: center;
  gap: 15px;
  padding-right: 45px;
}

.fp-profile-avatar {
  width: 64px;
  height: 64px;
  border-radius: 17px;
  background: linear-gradient(135deg, #3E0F8D, #9564DD);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 25px;
  font-weight: 800;
}

.fp-profile-header h2 {
  margin: 0;
  font-size: 22px;
  color: #2c2236;
}

.fp-profile-role {
  display: inline-block;
  margin-top: 5px;
  font-size: 12px;
  color: #7d7387;
}

.fp-profile-info-grid {
  margin-top: 24px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.fp-profile-info {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 13px;
  border: 1px solid #ece8f0;
  border-radius: 11px;
  background: #fcfbfd;
}

.fp-profile-info svg {
  color: #6f42b2;
  margin-top: 2px;
  flex-shrink: 0;
}

.fp-profile-info span {
  display: block;
  color: #89818e;
  font-size: 10px;
  margin-bottom: 4px;
}

.fp-profile-info strong {
  font-size: 12px;
  color: #34283e;
  word-break: break-word;
}

.fp-profile-section {
  margin-top: 22px;
}

.fp-profile-section h3 {
  margin: 0 0 11px;
  color: #34283e;
  font-size: 14px;
}

.fp-profile-skills {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.fp-profile-skills span {
  padding: 6px 9px;
  border-radius: 7px;
  background: #f0eafa;
  color: #6034a5;
  font-size: 11px;
  font-weight: 700;
}

.fp-profile-skills p {
  margin: 0;
  color: #8e8694;
  font-size: 12px;
}

.fp-remark {
  margin: 0;
  border-radius: 10px;
  padding: 12px;
  background: #faf8fc;
  border: 1px solid #eee9f3;
  color: #685e70;
  font-size: 12px;
  line-height: 1.6;
}

.fp-profile-footer {
  margin-top: 25px;
  padding-top: 16px;
  border-top: 1px solid #ece8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.fp-profile-footer span {
  color: #7f7685;
  font-size: 12px;
}

.fp-profile-footer strong {
  color: #3E0F8D;
}

.fp-profile-footer button {
  border: none;
  background: #3E0F8D;
  color: white;
  border-radius: 8px;
  padding: 9px 15px;
  font-weight: 700;
  cursor: pointer;
}

/* =========================================================
   LOADING
========================================================= */

.fp-loading-page {
  display: flex;
  align-items: center;
  justify-content: center;
}

.fp-loader-box {
  background: white;
  border: 1px solid #e7e1ed;
  border-radius: 17px;
  padding: 38px;
  text-align: center;
  box-shadow: 0 15px 40px rgba(45, 28, 58, 0.07);
}

.fp-loader-box h3 {
  margin: 15px 0 5px;
  color: #33263d;
}

.fp-loader-box p {
  margin: 0;
  color: #867d8d;
  font-size: 12px;
}

.fp-spinner {
  color: #3E0F8D;
}

/* =========================================================
   ANIMATION
========================================================= */

.fp-spin {
  animation: fp-spin 0.9s linear infinite;
}

@keyframes fp-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

/* =========================================================
   RESPONSIVE
========================================================= */

@media (max-width: 1200px) {
  .fp-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .fp-header {
    flex-direction: column;
    align-items: stretch;
  }

  .fp-header-actions {
    justify-content: flex-end;
  }

  .fp-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 650px) {
  .fp-page {
    padding: 16px;
  }

  .fp-header-actions {
    flex-wrap: wrap;
  }

  .fp-count-box {
    flex: 1;
  }

  .fp-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .fp-search-box {
    max-width: none;
  }

  .fp-form-grid {
    grid-template-columns: 1fr;
  }

  .fp-form-group.fp-full {
    grid-column: auto;
  }

  .fp-profile-info-grid {
    grid-template-columns: 1fr;
  }

  .fp-modal-overlay {
    padding: 10px;
  }

  .fp-create-modal,
  .fp-profile-modal {
    border-radius: 14px;
  }

  .fp-modal-header,
  .fp-form-body,
  .fp-modal-footer {
    padding-left: 17px;
    padding-right: 17px;
  }

  .fp-modal-footer {
    flex-direction: column;
  }

  .fp-cancel-btn,
  .fp-create-btn {
    width: 100%;
    justify-content: center;
  }
}
`;

export default FreelancePro;